import React, { useEffect } from "react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { store, persistor } from "./store";
import { PersistGate } from "redux-persist/integration/react";
import {
  HashRouter as Router,
  Switch,
  Redirect,
  useHistory 
} from "react-router-dom";
import "moment/locale/es";
import { Route } from "components";
import { RootState } from "reducers";
import { emit } from 'jetemit';

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";
import { ConversionService, AuthService, ClientHomeService } from 'services';
import { Socket, SocketEvents, Constants } from 'utils';

// Public
import Home from 'screens/home';
import JobApplication from 'screens/client/job-application';
import BFC from 'screens/bfc';
import PublicPayment from 'screens/public/public-payment';
import Referral from 'screens/referral';
import Survey from 'screens/survey';

// Client
import Client from 'screens/client/home';
import ClientProfile from 'screens/client/profile/profile';
import ClientQuotes from 'screens/client/quotes/quotes';
import ClientRequestQuote from 'screens/client/quotes/request';
import ClientUsers from 'screens/client/users/users';
import ClientPoints from 'screens/client/points/points';

// Auth
import Reset from "screens/auth/login/reset";

// Admin
import Admin from 'screens/admin/home';
import AdminModerators from 'screens/admin/moderators/moderators';
import AdminServices from 'screens/admin/services/services';
import AdminOperators from 'screens/admin/operators/operators';
import AdminQuotes from 'screens/admin/quotes/quotes';
import AdminClients from 'screens/admin/clients/clients';
import AdminBankAccounts from 'screens/admin/bank-accounts/bank-accounts';
import AdminDiscounts from 'screens/admin/discounts/discounts';
import AdminAccessories from 'screens/admin/accessories/accessories';
import AdminCorporatePayment from 'screens/admin/corporate-payments/corporate-payments';
import AdminJobApplications from 'screens/admin/job-applications/job-applications';
import AdminReports from 'screens/admin/reports/reports';
import AdminReportsServices from 'screens/admin/reports/services';
import AdminReportsRecurringServices from 'screens/admin/reports/recurring-services';
import AdminReportsSurveys from 'screens/admin/reports/surveys';
import AdminReportsPlanning from 'screens/admin/reports/planning';
import AdminPoints from 'screens/admin/points/points';
import AdminPlanning from 'screens/admin/planning/planning';
import AdminExcludedPlaces from 'screens/admin/excluded-places/excluded-places';
import AdminJourneys from 'screens/admin/journeys/journeys';
import AdminQuoteTypes from 'screens/admin/quote-types/quote-types';

// Shop
import AdminShopHome from 'screens/admin/shop/home';
import AdminShopProducts from 'screens/admin/shop/products/products';
import AdminShopInventory from 'screens/admin/shop/inventory/inventory';
import AdminShopCategories from 'screens/admin/shop/categories/categories';
import AdminShopCombos from 'screens/admin/shop/combos/combos';
import AdminShopBanners from 'screens/admin/shop/banners/banners';
import AdminShopOrders from 'screens/admin/shop/orders/orders';

// Scripts
import "jquery/dist/jquery.min.js";
import "bootstrap/dist/js/bootstrap.min.js";

// Styles
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "./scss/main.scss";

const Container = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state: RootState) => state.user);

  useEffect(() => {
    const get = async () => {
      let res: any = null;
      res = await ConversionService.admin.get({
        withoutLoading: true
      });
      if (res.conversion) {
        dispatch({
          type: 'SET_CONVERSION',
          payload: res.conversion
        });
      }
      if (res.surcharge_title) {
        dispatch({
          type: 'SET_SURCHARGE_TITLE',
          payload: res.surcharge_title
        });
      }
      res = await ClientHomeService.client.getContactInfo({
        withoutLoading: true
      });
      if (res.contact_info) {
        dispatch({
          type: 'SET_CONTACT_INFO',
          payload: res.contact_info
        });
      }
    }
    get();

    const getUser = async () => {
      if (user) {
        const res: any = await AuthService.checkUser({
          user_id: user.id,
          withoutLoading: true
        });
        if (res.user) {
          await dispatch({
            type: 'SET_USER',
            payload: res.user
          });
          emit(SocketEvents.UPDATE_USER);
          if (res.user?.verified == Constants.VALIDATION_STATUS.INACTIVE && res.user?.level_id == Constants.LEVELS.CLIENT) {
            history.replace('/client/quotes/request');
          }
        }
      }
    }
    getUser();

    Socket.on(SocketEvents.CONVERSION.UPDATE,() => {
      get();
    });

    Socket.on(SocketEvents.UPDATE_USER,(data: any) => {
      const user = store.getState().user;
      if (user != null && user?.id == data.user_id) {
        getUser();
      }
    });
  },[]);

  return (
    <React.Fragment>
      <Switch>
        <Route exact path="/" component={ Home } />
        <Route exact path="/job-application" component={ JobApplication } />
        <Route exact path="/public-payment/:code" component={ PublicPayment } />
        <Route exact path="/bfc" component={ BFC } />
        <Route exact path="/referral/:code" component={ Referral } />
        <Route exact path="/survey/:code" component={ Survey } />

        {/* Auth */}
        <Route exact noAuth path="/recover/:code" component={ Reset } />

        {/* Client */}
        <Route exact client path="/home" component={ Client } />
        <Route exact client path="/client/profile" component={ ClientProfile } />
        <Route exact client path="/client/quotes" component={ ClientQuotes } />
        <Route exact client path="/client/users" component={ ClientUsers } />
        <Route exact withLayout path="/client/quotes/request/:service_id?" component={ ClientRequestQuote } />
        <Route exact client path="/client/points" component={ ClientPoints } />
        <Route exact client path="/client/discounts" component={ AdminDiscounts } />

        {/* Admin */}
        <Route exact admin path="/admin" component={ Admin } />  
        <Route exact admin path="/admin/moderators" component={ AdminModerators } permission={ Constants.MODULES.MODERATORS } />  
        <Route exact admin path="/admin/accessories" component={ AdminAccessories } permission={ Constants.MODULES.ACCESSORIES } />  
        <Route exact admin path="/admin/services" component={ AdminServices } permission={ Constants.MODULES.SERVICES } /> 
        <Route exact admin path="/admin/operators" component={ AdminOperators } permission={ Constants.MODULES.OPERATORS } />  
        <Route exact admin path="/admin/quotes" component={ AdminQuotes } permission={ Constants.MODULES.QUOTES } /> 
        <Route exact admin path="/admin/clients" component={ AdminClients } permission={ Constants.MODULES.CLIENTS } /> 
        <Route exact admin path="/admin/bank-accounts" component={ AdminBankAccounts } permission={ Constants.MODULES.BANK_ACCOUNTS } /> 
        <Route exact admin path="/admin/discounts" component={ AdminDiscounts } permission={ Constants.MODULES.DISCOUNTS } /> 
        <Route exact admin path="/admin/corporate-payments" component={ AdminCorporatePayment } permission={ Constants.MODULES.CORPORATE_PAYMENTS } /> 
        <Route exact admin path="/admin/job-applications" component={ AdminJobApplications } permission={ Constants.MODULES.JOB_APPLICATIONS } /> 
        <Route exact admin path="/admin/reports" component={ AdminReports } permission={ Constants.MODULES.REPORTS } /> 
        <Route exact admin path="/admin/reports/services" component={ AdminReportsServices } permission={ Constants.MODULES.REPORTS } /> 
        <Route exact admin path="/admin/reports/recurring-services" component={ AdminReportsRecurringServices } permission={ Constants.MODULES.REPORTS } />
        <Route exact admin path="/admin/reports/surveys" component={ AdminReportsSurveys } permission={ Constants.MODULES.REPORTS } />
        <Route exact admin path="/admin/reports/planning" component={ AdminReportsPlanning } permission={ Constants.MODULES.REPORTS } />
        <Route exact admin path="/admin/points" component={ AdminPoints } permission={ Constants.MODULES.POINTS } />
        <Route exact admin path="/admin/planning" component={ AdminPlanning } permission={ Constants.MODULES.PLANNING } />
        <Route exact admin path="/admin/excluded-places" component={ AdminExcludedPlaces } permission={ Constants.MODULES.EXCLUDED_PLACES } />
        {/*<Route exact admin path="/admin/journeys" component={ AdminJourneys } permission={ Constants.MODULES.JOURNEYS } />*/}
        <Route exact admin path="/admin/quote-types" component={ AdminQuoteTypes } permission={ Constants.MODULES.QUOTE_TYPES } />

        {/* Shop */}
        <Route exact admin path="/admin/shop" component={ AdminShopHome } permissions={ [
          Constants.MODULES.ORDERS,
          Constants.MODULES.INVENTORY,
          Constants.MODULES.PRODUCTS,
          Constants.MODULES.COMBOS,
          Constants.MODULES.BANNERS,
          Constants.MODULES.CATEGORIES
        ] } />  
        <Route exact admin path="/admin/shop/products" component={ AdminShopProducts } permission={ Constants.MODULES.PRODUCTS } />  
        <Route exact admin path="/admin/shop/inventory" component={ AdminShopInventory } permission={ Constants.MODULES.INVENTORY } />  
        <Route exact admin path="/admin/shop/combos" component={ AdminShopCombos } permission={ Constants.MODULES.COMBOS } />  
        <Route exact admin path="/admin/shop/categories" component={ AdminShopCategories } permission={ Constants.MODULES.CATEGORIES } />  
        <Route exact admin path="/admin/shop/banners" component={ AdminShopBanners } permission={ Constants.MODULES.BANNERS } />  
        <Route exact admin path="/admin/shop/orders" component={ AdminShopOrders } permission={ Constants.MODULES.ORDERS } />  

        <Redirect to="/" />
      </Switch>
    </React.Fragment>
  )
}

const App = () => (
  <Provider store={ store }>
    <PersistGate loading={ null } persistor={ persistor }>
      <Router>
        <Container />
      </Router>
    </PersistGate>
  </Provider>
);

export default App;
